import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, signal, WritableSignal } from '@angular/core';
import { FilterMatchMode, SelectItem } from 'primeng/api';
import { PrimeNG } from 'primeng/config';
import { Observable, Subject } from 'rxjs';
import { Country } from '../../content/interfaces/country';
import { SpsMenuItem } from '../../layout/interfaces/sps-menu-item';
import { I18nTranslations } from '../interfaces/i18n-translations';

@Injectable({
  providedIn: 'root'
})
export class I18nService {

  readonly activeLanguageKey = signal('');
  readonly i18nLng: WritableSignal<I18nTranslations> = signal(null);
  readonly i18nInitialized: WritableSignal<boolean> = signal(false);
  private i18nServiceUrl: string = 'i18n/';

  /**
   * available languages and current active language
   * for language selection in profile dialog
   */
  availableLanguages: SelectItem[] = [];
  activeLanguage: SelectItem = null;

  /**
   * available countries de and en
   */
  countries: Country[] = [];


  readonly breadcrumbMenuItems: WritableSignal<SpsMenuItem[]> =  signal(null);

  languageChanged = new Subject<boolean>();

    constructor(private http: HttpClient,
              private primeng: PrimeNG) {
    this.initAvailableLanguages();
  }

  /**
   * init list with available languages
   * TODO store and fetch from backend
   * @private
   */
  private initAvailableLanguages() {
    this.getAvailableLanguages().subscribe({
      next: (languages) => {
        this.availableLanguages = languages;
      }, error: err => {
        console.log('Error initializing available Languages: ' + err.message());
      }
    })
  }

  /**
   * init ptimeng specific i18n data
   * @private
   */
  initPrimengTranslation(languageKey: string) {
    this.getPrimeTranslationData(languageKey).subscribe({
      next: (i18nPrime) => {
        this.primeng.setTranslation(i18nPrime);

        /* FilterMatchModes setzen */
        this.primeng.filterMatchModeOptions = {
          text: [
            FilterMatchMode.STARTS_WITH,
            FilterMatchMode.CONTAINS,
            FilterMatchMode.EQUALS
          ],
          numeric: [
            FilterMatchMode.EQUALS,
            FilterMatchMode.LESS_THAN,
            FilterMatchMode.GREATER_THAN
          ],
          date: [
            FilterMatchMode.DATE_IS,
            // FilterMatchMode.DATE_BEFORE,
            // FilterMatchMode.DATE_AFTER,
          ]
        };

        this.activeLanguageKey.set(languageKey);
      },
      error: err => {
        console.log('Error initializing PrimeNG-Translation: ' + err.message());
      }
    })
  }

  /**
   * init list with translation data
   * TODO store and fetch from backend
   * @private
   */
  initLanguageData(languageKey: string) {
    this.getLanguageData(languageKey).subscribe({
      next: (lngData) => {
        this.i18nLng.set(lngData);
        this.i18nInitialized.set(true);
        this.activeLanguage = this.availableLanguages.find(item => item.value === languageKey);
        this.refreshBreadcrumbItems()
      }, error: err => {
        console.log('Error initializing Language-Data: ' + err.message());
      }
    })
  }

  /**
   * Static list of available countries de and en
   */
  initCountryList() {
    this.getCountryList().subscribe({
      next: (countries) => {
        this.countries = countries;
      }, error: err => {
        console.log('Error initializing Countries Data: ' + err.message());
      }
    });
  }

  getCountryById(id: string): string {
    const country: Country = this.countries.find(item => item.code === id);
    if (country) {
      return country[this.activeLanguageKey()];
    }
    return '';
  }

  /**
   * key builder for parameterized i18n-keys
   * for example: CUSTOMER_IN_PROGRESS_?
   * @param key
   * @param param
   */
  paramKeyBuilder(key: string, param: string): string {
    return key.replace("?", param);
  }

  /**
   * Refresh Breadcrumb after language changed
   * force refresh is necessary
   * @private
   */
  private refreshBreadcrumbItems() {
    if (this.breadcrumbMenuItems()) {
      let tmpBcItems: SpsMenuItem[] = [];
      for (let menuItem of this.breadcrumbMenuItems()) {
        menuItem.label = this.i18nLng()[menuItem.i18nLabelKey];
        tmpBcItems.push(menuItem);
      }
      this.breadcrumbMenuItems.set([]);
      this.languageChanged.next(true);
      setTimeout(() => {
        this.breadcrumbMenuItems.set(tmpBcItems);
      }, 50);
    }
  }

  /**
   * i18n-Translation-Data for current language from assets (public folder)
   * TODO should be in database and managed with devtools
   * @param languageKey
   */
  private getAvailableLanguages(): Observable<any> {
    let headers = new HttpHeaders().set('content-type', 'application/json; charset=utf-8');
    return this.http.get<any>(this.i18nServiceUrl + 'languages/' + 'languages.json', {headers});
  }

  /**
   * Primeng i18n-Data for translation and language component settings from assets (public folder)
   * @param languageKey
   */
   private getPrimeTranslationData(languageKey: string): Observable<any> {
    let headers = new HttpHeaders().set('content-type', 'application/json; charset=utf-8');
    return this.http.get<any>(this.i18nServiceUrl + 'primeng/' + languageKey + '.json', {headers});
  }

  /**
   * i18n-Translation-Data for current language from assets (public folder)
   * TODO should be in database and managed with devtools
   * @param languageKey
   */
   private getLanguageData(languageKey: string): Observable<any> {
    let headers = new HttpHeaders().set('content-type', 'application/json; charset=utf-8');
    return this.http.get<any>(this.i18nServiceUrl + 'languages/' + languageKey + '.json', {headers});
  }

  /**
   * get country-list from public folder
   * @private
   */
  private getCountryList(): Observable<Country[]> {
    let headers = new HttpHeaders().set('content-type', 'application/json; charset=utf-8');
    return this.http.get<any>('data/countries.json', {headers});
  }

}
